/**
 * Contrato entre as telas e o dado.
 *
 * As telas so conhecem esta interface. Duas implementacoes: a demo (dados
 * ficticios, so leitura) e a local (banco do aparelho, com cadastro), escolhidas
 * pela chave "Modo demonstracao". Tudo e assincrono.
 *
 * As linhas seguem os nomes das tabelas e views do banco (snake_case).
 */

export type Sexo = 'macho_inteiro' | 'castrado' | 'femea';

/** Linha de `stud_farm`. */
export interface Haras {
  id: string;
  name: string;
  city: string | null;
  uf: string | null;
  abccmm_code: string | null;
  owner_contact: string | null;
  data_sharing_level: 'privado' | 'agregado' | 'publico';
  notes: string | null;
}

/** Linha de `animal`. */
export interface Animal {
  id: string;
  abccmm_registry: string | null;
  name: string;
  sex: Sexo;
  birth_date: string;
  /** Data estimada (pai ou mae de fora da base): so o ano e conhecido. */
  birth_date_estimated: boolean;
  stud_book: string | null;
  microchip: string | null;
  coat_color: string | null;
  marcha_type_declared: 'batida' | 'picada' | null;
  owner_farm_id: string | null;
  breeder_farm_id: string | null;
  sire_id: string | null;
  dam_id: string | null;
  notes: string | null;
  /** So na demonstracao: animal de verdade no meio dos ficticios (ver dados/real). */
  real?: DadosReais;
}

/**
 * Um animal de verdade levado a demonstracao (apresentacao do projeto). Nunca
 * vem do banco: o que foi estimado aqui nao e dado e nao pode virar um.
 */
export interface DadosReais {
  /** Quem coletou e quando: "Rafael, 17/10/2026". */
  coleta: string;
  /** Avisos para quem le: idade a confirmar, foto de outro dia... */
  observacoes: string[];
  /** Medidas coletadas que a ficha v1.0 nao tem, no grupo da ficha em que se encaixam. */
  extras: MedidaForaDoProtocolo[];
  /** Id da foto de perfil com os pontos, para abrir os angulos marcados. */
  fotoDosAngulos?: string;
}

/** Medida que a ficha v1.0 nao tem: aparece no grupo dela, marcada "fora da v1.0". */
export interface MedidaForaDoProtocolo {
  grupo: 'comprimento' | 'perimetro';
  rotulo: string;
  valor: number;
  unidade: string;
  /** Como veio na mensagem de quem mediu, quando o rotulo foi adaptado. */
  original?: string;
}

/** De onde veio o valor: medido no animal, tirado da foto ou estimado. */
export type OrigemDoValor = 'campo' | 'foto' | 'estimado';

/** Linha de `visit` (o que a pre-visita precisa). */
export interface Visita {
  id: string;
  stud_farm_id: string;
  visit_number: number;
  visit_date: string;
  protocol_version_id: string;
  /** Fechamento do dia: a visita encerrada no haras, com todas as fichas fechadas. */
  closed_at: string | null;
  /** Rotina, ou estudo de repetibilidade da Fase 0 (varios avaliadores x 2 rodadas). */
  purpose: 'rotina' | 'estudo_repetibilidade';
  weather: 'sol' | 'nublado' | 'chuva' | null;
  temp_c: number | null;
  notes: string | null;
}

/** `assessment` com a data da visita — os campos de identidade de `v_measurement_long`. */
export interface Avaliacao {
  id: string;
  animal_id: string;
  visit_id: string;
  visit_date: string;
  protocol_level: 1 | 2 | 3;
  status: 'rascunho' | 'fechada' | 'validada' | 'reaberta';
  /** Marcado quando a coleta da ficha comeca. */
  started_at: string | null;
  /** Marcado no fechamento. Com `started_at`, da o tempo da ficha. */
  ended_at: string | null;
  /** Sorteada para a dupla medicao (segunda sessao, outro avaliador, as cegas). */
  sampled_for_double_measure: boolean;
}

/** Uma observacao, como em `v_measurement_long` (ja sem as substituidas). */
export interface Medida {
  /** Id da linha de `measurement` (fonte local; a demo nao tem). */
  id?: string;
  assessment_id: string;
  trait_code: string;
  side: 'E' | 'D' | 'NA';
  value_num: number | null;
  not_applicable: boolean;
  /** Ausente = medido no campo, como em toda ficha do banco. So o animal real da demonstracao preenche. */
  origem?: OrigemDoValor;
  /** Como o valor foi obtido, quando nao foi medido direto. */
  como?: string;
  /** Por que o valor precisa ser conferido. */
  conferir?: string;
}

/**
 * Uma observacao de QUALQUER sessao, no formato longo (como v_measurement_long):
 * a materia-prima da exportacao e do relatorio de repetibilidade.
 */
export interface Observacao {
  assessment_id: string;
  avaliador: string;
  rodada: number;
  metodo: 'campo' | 'foto' | 'sensor';
  trait_code: string;
  side: Medida['side'];
  value_num: number | null;
  not_applicable: boolean;
}

/** Linha de `media`, com o andamento de `gait_trial` quando o video e de pista. */
export interface Midia {
  id: string;
  assessment_id: string;
  kind: 'foto' | 'video';
  view: 'perfil_esquerdo' | 'frente' | 'tras' | 'pista' | null;
  gait: 'passo' | 'marcha_curta' | 'marcha_media' | 'marcha_longa' | null;
  /** Nulo enquanto o arquivo nao foi enviado ao armazenamento. */
  storage_path: string | null;
  /** O arquivo esta neste aparelho, esperando rede para subir. */
  pendente: boolean;
  /** Cor do adesivo na foto (`#rrggbb`), para a deteccao automatica. */
  marker_color: string | null;
  /** Distancia da camera ao cavalo, em metros. */
  camera_distance_m: number | null;
}

/** O que o avaliador informa junto da foto de perfil. */
export interface DadosDaFoto {
  corDoMarcador?: string | null;
  distanciaM?: number | null;
}

export type VistaDeFoto = 'perfil_esquerdo' | 'frente' | 'tras';
export type Andamento = NonNullable<Midia['gait']>;

/**
 * Uma versao da analise de uma midia (linha de `media_analysis`). Editar grava
 * uma versao nova com `supersedes` apontando para a anterior; nada e reescrito.
 */
export interface AnaliseDeMidia {
  id: string;
  media_id: string;
  author_id: string;
  /** Nome do autor, resolvido de `person` para exibir. */
  author_name: string;
  body: string;
  supersedes: string | null;
  created_at: string;
  deleted_at: string | null;
}

export interface Usuario {
  id: string;
  nome: string;
  /** `person.app_role`. So admin e avaliador escrevem analise. */
  papel: 'admin' | 'avaliador' | 'leitor' | 'haras';
}

export const podeAnalisar = (u: Usuario | null) => u?.papel === 'admin' || u?.papel === 'avaliador';
/** Cadastro de haras, animais e visitas: so a equipe. O dono do haras so le. */
export const podeCadastrar = podeAnalisar;

/** Linha de `person` como o admin a ve, com os haras que ela enxerga. */
export interface PessoaDaEquipe {
  id: string;
  nome: string;
  email: string | null;
  papel: Usuario['papel'];
  ativo: boolean;
  /** Ja entrou pelo menos uma vez (login ligado pelo e-mail). */
  vinculada: boolean;
  harasIds: string[];
}

/** Pessoa da equipe tecnica: quem pode medir. */
export interface Pessoa {
  id: string;
  nome: string;
}

/** Estacao 1 (condicao e manejo): as colunas de `animal_management`. */
export interface Manejo {
  weight_kg: number | null;
  weight_method: 'balanca' | 'fita' | null;
  bcs_henneke: number | null;
  last_feed_time: string | null;
  water_access: 'livre' | 'restrita' | null;
  arrived_farm_gt_30d: boolean | null;
  female_status: 'vazia' | 'prenhe' | 'parida' | 'lactando' | 'doadora' | null;
  gestation_months: number | null;
  days_postpartum: number | null;
  male_status: 'em_reproducao' | 'fora_de_reproducao' | null;
  covers_last_week: number | null;
  training_status: 'sem_trabalho' | 'em_doma' | 'em_trabalho' | 'preparo_exposicao' | null;
  training_months: number | null;
  last_shoeing_date: string | null;
  shod_fl: boolean;
  shod_fr: boolean;
  shod_hl: boolean;
  shod_hr: boolean;
  barefoot: boolean;
  hoof_condition: 'normais' | 'achinelados' | 'encastelados' | null;
  fitness: 'apto' | 'claudicacao' | 'lesao' | null;
  lameness_limb: 'AE' | 'AD' | 'PE' | 'PD' | null;
  lameness_aaep: number | null;
  dna_sample_collected: boolean;
  dna_tube_id: string | null;
}

export interface SessaoDeCampo {
  id: string;
  evaluator_id: string;
  recorder_id: string | null;
}

/** Uma sessao com as proprias medidas (rotina: 1a e 2a medicao; estudo: avaliador x rodada). */
export interface SessaoComMedidas extends SessaoDeCampo {
  replicate_no: number;
  medidas: Medida[];
}

/** Pontos marcados na foto de perfil, em coordenadas 0-1 da imagem. */
export type PontosMarcados = Record<string, { x: number; y: number }>;

/** De onde veio cada ponto: achado pela deteccao automatica ou marcado a mao. */
export type OrigemDosPontos = Record<string, 'auto' | 'manual'>;

/** A medicao por foto de uma ficha: os pontos marcados e os angulos que sairam deles. */
export interface MedicaoPorFoto {
  midiaId: string | null;
  pontos: PontosMarcados;
  origem: OrigemDosPontos;
  angulos: Record<string, number>;
  /** Quem marcou (nome), quando houver. */
  autor: string | null;
}

/** Tudo o que a tela de coleta precisa de uma ficha. */
export interface FichaDeColeta {
  avaliacao: Avaliacao;
  visita: Visita;
  animal: Animal;
  /** Sessao de campo da rodada normal (replicate 1). Nula antes de escolher quem mede. */
  sessao: SessaoDeCampo | null;
  medidas: Medida[];
  /** Dupla medicao: a segunda sessao (replicate 2) e as medidas dela. */
  sessao2: SessaoDeCampo | null;
  medidas2: Medida[];
  /** Todas as sessoes de campo da ficha, com as medidas de cada uma. */
  sessoes: SessaoComMedidas[];
  manejo: Manejo;
  midias: Midia[];
  /** Checklist dos marcadores M1-M18 da Estacao 2 (so os ja marcados). */
  marcadores: Record<string, boolean>;
}

/** Uma correcao auditavel de ficha fechada (linha de `amendment`). */
export interface Emenda {
  id: string;
  entity: string;
  entity_id: string;
  field: string;
  old_value: string | null;
  new_value: string | null;
  reason: string;
  at: string;
  /** Nome de quem fez, resolvido de `person`. */
  autor: string;
}

export type HarasEntrada = Omit<Haras, 'id'> & { id?: string };
export type AnimalEntrada = Omit<Animal, 'id'> & { id?: string };

/** Escrita. Existe so na fonte local; na demo as acoes de cadastro nem aparecem. */
export interface CadastroDeDados {
  salvarHaras(h: HarasEntrada): Promise<string>;
  salvarAnimal(a: AnimalEntrada): Promise<string>;
  /** Delete logico: sai do plantel, o registro fica no banco. */
  arquivarAnimal(id: string): Promise<void>;
  escolherHaras(id: string): void;
  visitas(): Promise<Visita[]>;
  visita(id: string): Promise<Visita | null>;
  planejarVisita(v: {
    visit_date: string;
    visit_number: number;
    purpose?: Visita['purpose'];
    itens: readonly { animal_id: string; protocol_level: 1 | 2 }[];
  }): Promise<string>;
  incluirNaVisita(visitId: string, animalId: string, nivel: 1 | 2): Promise<void>;
  /** So avaliacao em rascunho; delete logico. */
  retirarDaVisita(avaliacaoId: string): Promise<void>;

  // --- Coleta (Nivel 1) ---
  ficha(avaliacaoId: string): Promise<FichaDeColeta | null>;
  /** Admin e avaliadores: quem pode medir. */
  pessoasDaEquipe(): Promise<Pessoa[]>;
  /**
   * Abre (ou troca o medidor de) a sessao de campo. Quem registra e o usuario
   * logado. `replica` 2 e a segunda medicao da ficha sorteada, por outro avaliador.
   */
  abrirSessao(avaliacaoId: string, medidorId: string, replica?: 1 | 2): Promise<string>;
  /**
   * Grava uma observacao. `valor` null sem `naoSeAplica` apaga (delete logico);
   * `naoSeAplica` grava a coluna NA da ficha (o item nao se aplica ao animal).
   */
  salvarMedida(
    sessaoId: string,
    traitCode: string,
    side: Medida['side'],
    valor: number | null,
    naoSeAplica?: boolean,
  ): Promise<void>;
  salvarManejo(avaliacaoId: string, manejo: Partial<Manejo>): Promise<void>;
  /** Data, numero e condicoes da visita (so enquanto ela nao foi encerrada). */
  salvarVisita(
    visitaId: string,
    dados: Partial<Pick<Visita, 'visit_date' | 'visit_number' | 'weather' | 'temp_c' | 'notes'>>,
  ): Promise<void>;
  /** Estacao 2: marcador colado (ou nao) no animal. */
  marcarMarcador(avaliacaoId: string, marcador: string, colado: boolean): Promise<void>;
  /** Fechamento do dia: so com todas as fichas da visita fechadas. */
  fecharVisita(visitaId: string): Promise<void>;
  /** Troca o nivel de uma ficha ainda aberta (ex.: animal chegou ha menos de 30 dias). */
  mudarNivel(avaliacaoId: string, nivel: 1 | 2): Promise<void>;
  // --- Equipe (so admin) ---
  equipe(): Promise<PessoaDaEquipe[]>;
  salvarPessoa(p: { id?: string; full_name: string; email: string; app_role: Usuario['papel']; active: boolean }): Promise<string>;
  /** Os haras que um dono enxerga: cria os acessos que faltam e revoga os que sairam. */
  definirAcessos(personId: string, harasIds: readonly string[]): Promise<void>;

  // --- Fotogrametria (no escritorio, com a ficha ja fechada) ---
  medicaoPorFoto(avaliacaoId: string): Promise<MedicaoPorFoto>;
  salvarMedicaoPorFoto(
    avaliacaoId: string,
    midiaId: string,
    pontos: PontosMarcados,
    angulos: Readonly<Record<string, number | null>>,
    /** Origem de cada ponto e versao do detector: medem quanto o avaliador corrige. */
    deteccao?: { origem: OrigemDosPontos; detector: string | null },
  ): Promise<void>;

  // --- Midia ---
  /** Guarda a foto no aparelho e a linha de `media`; o arquivo sobe quando houver rede. */
  adicionarFoto(avaliacaoId: string, vista: VistaDeFoto, arquivo: File, dados?: DadosDaFoto): Promise<string>;
  /** Cor do adesivo e distancia da camera de uma foto ja guardada (vale com a ficha fechada). */
  salvarDadosDaFoto(midiaId: string, dados: DadosDaFoto): Promise<void>;
  /** Video de marcha (camera propria), com o andamento em `gait_trial`. */
  adicionarVideo(avaliacaoId: string, vista: 'frente' | 'tras', andamento: Andamento, arquivo: File): Promise<string>;
  /** Delete logico da `media`; o arquivo que ainda nao subiu sai do aparelho. */
  removerMidia(id: string): Promise<void>;

  // --- Emendas (ficha fechada) ---
  /** Corrige uma medida da ficha fechada: novo valor, ou "nao se aplica". */
  emendarMedida(medidaId: string, correcao: { valor: number | null; naoSeAplica: boolean }, motivo: string): Promise<void>;
  /** Corrige um campo da Estacao 1 da ficha fechada. */
  emendarManejo(avaliacaoId: string, campo: keyof Manejo, novo: Manejo[keyof Manejo], motivo: string): Promise<void>;
  /** So admin. */
  reabrirFicha(avaliacaoId: string, motivo: string): Promise<void>;
  /** So admin. */
  validarFicha(avaliacaoId: string, observacao: string): Promise<void>;
  /** Historico de emendas da ficha, da mais nova para a mais antiga. */
  emendas(avaliacaoId: string): Promise<Emenda[]>;

  /** Grava a justificativa de cada aviso ignorado e fecha a ficha. */
  fecharFicha(avaliacaoId: string, justificativas: Readonly<Record<string, string>>): Promise<void>;
}

/** Algo que emite o resultado de novo sempre que o dado muda (o liveQuery do Dexie). */
export interface Observavel<T> {
  subscribe(o: { next: (v: T) => void; error: (e: unknown) => void }): { unsubscribe(): void };
}

export interface FonteDeDados {
  /** Fonte de demonstracao? A interface avisa o usuario. */
  readonly ficticia: boolean;
  /** Presente so onde da para gravar (fonte local). */
  readonly cadastro?: CadastroDeDados;
  /** Medicao por foto so para ver, onde nao se grava (demonstracao). */
  medicaoPorFoto?(avaliacaoId: string): Promise<MedicaoPorFoto>;
  /** Reexecuta uma consulta sempre que o dado muda. Ausente = consulta unica. */
  observar?<T>(consulta: () => Promise<T>): Observavel<T>;
  /** O haras em que se esta trabalhando. */
  haras(): Promise<Haras | null>;
  listarHaras(): Promise<Haras[]>;
  /** Todos os animais (de qualquer haras, e pais de fora da base). O plantel filtra pelo haras. */
  listarAnimais(): Promise<Animal[]>;
  animal(id: string): Promise<Animal | null>;
  filhos(animalId: string): Promise<Animal[]>;
  /** Avaliacoes do animal, da mais antiga para a mais recente. Sem `animalId`, todas. */
  avaliacoes(animalId?: string): Promise<Avaliacao[]>;
  medidas(avaliacaoIds: readonly string[]): Promise<Medida[]>;
  /** Todas as sessoes (1a e 2a medicao, estudo, foto), para exportar e medir a repetibilidade. */
  observacoes(avaliacaoIds: readonly string[]): Promise<Observacao[]>;
  /** Visitas do haras atual, da mais recente para a mais antiga. */
  listarVisitas(): Promise<Visita[]>;
  /** Estacao 1 de cada ficha, por id (para a exportacao). */
  manejos(avaliacaoIds: readonly string[]): Promise<Record<string, Manejo>>;
  midias(avaliacaoId: string): Promise<Midia[]>;
  midia(id: string): Promise<Midia | null>;
  /**
   * Endereco para exibir o arquivo: o do aparelho (blob, revogar depois de usar)
   * ou um link assinado do Storage. Nulo quando nao ha arquivo disponivel.
   */
  urlDaMidia(m: Midia): Promise<string | null>;
  /** Foto de perfil mais recente de cada animal (a capa), por id do animal. */
  capas(): Promise<Map<string, Midia>>;
  /** Todas as versoes da analise da midia, da mais nova para a mais antiga. */
  analises(midiaId: string): Promise<AnaliseDeMidia[]>;
  /** Grava uma versao NOVA. Nunca edita a anterior. */
  salvarAnalise(nova: { media_id: string; body: string; supersedes: string | null }): Promise<AnaliseDeMidia>;
  usuarioAtual(): Promise<Usuario | null>;
}
